import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CalendarDays, ArrowRight, ShieldCheck, LayoutGrid, List as ListIcon, ArrowUp, ArrowDown, ChevronsUpDown, ChevronDown, ChevronRight } from "lucide-react";
import { useUiState } from "@/lib/uiState";

function AgentPill({ testid }) {
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 rounded-full bg-accent/20 text-primary" title="Class Agent assigned">
      <ShieldCheck className="h-3 w-3" /> Agent
    </span>
  );
}

export default function Classes() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useUiState("ud_classes_view", "grid");
  const [sort, setSort] = useUiState("ud_classes_sort", { key: "class_year", dir: "desc" });
  const [collapsed, setCollapsed] = useUiState("ud_classes_decades", {});
  const navigate = useNavigate();

  const toggleDecade = (d) => setCollapsed((c) => ({ ...c, [d]: !c[d] }));
  const decadeOf = (y) => Math.floor(Number(y) / 10) * 10;

  const setViewMode = (v) => { setView(v); };

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const sorted = [...classes].sort((a, b) => {
    const av = a[sort.key] ?? 0, bv = b[sort.key] ?? 0;
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sort.dir === "asc" ? cmp : -cmp;
  });

  const decadeGroups = (() => {
    const order = [];
    const map = {};
    for (const c of sorted) {
      const d = decadeOf(c.class_year);
      if (!map[d]) { map[d] = { decade: d, rows: [] }; order.push(d); }
      map[d].rows.push(c);
    }
    return order.map((d) => map[d]);
  })();
  const allCollapsed = decadeGroups.length > 0 && decadeGroups.every((g) => collapsed[g.decade]);
  const setAll = (val) => setCollapsed(() => { const o = {}; decadeGroups.forEach((g) => { o[g.decade] = val; }); return o; });

  const SortTh = ({ label, sortKey, align = "right" }) => (
    <th className={`px-4 py-2.5 ${align === "left" ? "text-left" : "text-right"}`}>
      <button
        data-testid={`classes-sort-${sortKey}`}
        onClick={() => toggleSort(sortKey)}
        className={`inline-flex items-center gap-1 uppercase font-mono text-xs hover:text-foreground transition-colors ${sort.key === sortKey ? "text-foreground" : ""}`}
      >
        {label}
        {sort.key === sortKey ? (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ChevronsUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </th>
  );

  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data.classes)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="fade-up">
      <div className="mb-6 flex items-end justify-between gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Class Management</div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mt-1">Classes</h1>
        </div>
        <div className="flex rounded-md border border-border overflow-hidden">
          <button data-testid="classes-view-grid" onClick={() => setViewMode("grid")} title="Grid view" className={`p-2 ${view === "grid" ? "bg-primary text-white" : "bg-card text-muted-foreground hover:bg-muted"}`}><LayoutGrid className="h-4 w-4" /></button>
          <button data-testid="classes-view-list" onClick={() => setViewMode("list")} title="List view" className={`p-2 border-l border-border ${view === "list" ? "bg-primary text-white" : "bg-card text-muted-foreground hover:bg-muted"}`}><ListIcon className="h-4 w-4" /></button>
        </div>
      </div>

      {loading && <div className="text-muted-foreground">Loading…</div>}
      {!loading && classes.length === 0 && <div className="text-muted-foreground">No classes yet.</div>}

      {!loading && view === "grid" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {classes.map((c) => (
            <button
              key={c.class_year}
              data-testid={`class-card-${c.class_year}`}
              onClick={() => navigate(`/classes/${c.class_year}`)}
              className="text-left bg-card border border-border rounded-lg shadow-sm p-5 hover:shadow-md hover:-translate-y-0.5 hover:border-primary transition-all group"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-primary min-w-0">
                  <CalendarDays className="h-5 w-5 shrink-0" />
                  <span className="font-serif text-2xl tracking-tight truncate">Class of {c.class_year}</span>
                </div>
                {c.has_agent ? <AgentPill testid={`classes-agent-pill-${c.class_year}`} /> : <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />}
              </div>
              <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                {[["Total", c.total], ["Living", c.living], ["Email", c.with_email], ["Address", c.with_address]].map(([l, v]) => (
                  <div key={l}>
                    <div className="text-xl font-semibold">{v}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">{l}</div>
                  </div>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}

      {!loading && view === "list" && classes.length > 0 && (
        <div data-testid="classes-list" className="bg-card border border-border rounded-lg shadow-sm overflow-hidden overflow-x-auto">
          <div className="flex justify-end px-4 py-2 border-b border-border">
            <button data-testid="classes-toggle-all" onClick={() => setAll(!allCollapsed)} className="text-xs font-mono uppercase tracking-wide text-muted-foreground hover:text-foreground flex items-center gap-1">
              {allCollapsed ? <><ChevronDown className="h-3.5 w-3.5" /> Expand all</> : <><ChevronRight className="h-3.5 w-3.5" /> Collapse all</>}
            </button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-xs uppercase font-mono">
              <tr>
                <SortTh label="Class" sortKey="class_year" align="left" />
                <SortTh label="Total" sortKey="total" />
                <SortTh label="Living" sortKey="living" />
                <SortTh label="Email" sortKey="with_email" />
                <SortTh label="Address" sortKey="with_address" />
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {decadeGroups.map((g) => {
                const isCollapsed = !!collapsed[g.decade];
                const agg = g.rows.reduce((s, c) => ({ total: s.total + (c.total || 0), living: s.living + (c.living || 0), email: s.email + (c.with_email || 0), addr: s.addr + (c.with_address || 0) }), { total: 0, living: 0, email: 0, addr: 0 });
                return (
                  <React.Fragment key={g.decade}>
                    <tr data-testid={`decade-row-${g.decade}`} onClick={() => toggleDecade(g.decade)} className="bg-muted/40 hover:bg-muted cursor-pointer border-t-2 border-border">
                      <td className="px-4 py-2 font-semibold">
                        <div className="flex items-center gap-2">
                          {isCollapsed ? <ChevronRight className="h-4 w-4 text-primary" /> : <ChevronDown className="h-4 w-4 text-primary" />}
                          <span className="font-serif text-base">{g.decade}s</span>
                          <span className="text-xs font-normal text-muted-foreground">({g.rows.length} classes)</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right font-semibold">{agg.total}</td>
                      <td className="px-4 py-2 text-right text-muted-foreground">{agg.living}</td>
                      <td className="px-4 py-2 text-right text-muted-foreground">{agg.email}</td>
                      <td className="px-4 py-2 text-right text-muted-foreground">{agg.addr}</td>
                      <td className="px-4 py-2"></td>
                    </tr>
                    {!isCollapsed && g.rows.map((c) => (
                      <tr key={c.class_year} data-testid={`class-row-${c.class_year}`} onClick={() => navigate(`/classes/${c.class_year}`)} className="hover:bg-muted/40 cursor-pointer">
                        <td className="px-4 py-2.5 pl-10">
                          <div className="flex items-center gap-2">
                            <CalendarDays className="h-4 w-4 text-primary shrink-0" />
                            <span className="font-medium">Class of {c.class_year}</span>
                            {c.has_agent && <AgentPill testid={`classes-agent-pill-${c.class_year}`} />}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-right font-medium">{c.total}</td>
                        <td className="px-4 py-2.5 text-right text-muted-foreground">{c.living}</td>
                        <td className="px-4 py-2.5 text-right text-muted-foreground">{c.with_email}</td>
                        <td className="px-4 py-2.5 text-right text-muted-foreground">{c.with_address}</td>
                        <td className="px-4 py-2.5 text-right"><ArrowRight className="h-4 w-4 text-muted-foreground inline" /></td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
